import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { HashRouter, BrowserRouter, Route, Routes } from 'react-router-dom';
import './index.css';
import { lazyWithRetry, LazyLoadingFallback } from './utils/lazyComponents';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import TitleBar from './components/TitleBar';

// Global chunk loading error handler for production
if (process.env.NODE_ENV === 'production' && process.env.CHUNK_RETRY_ENABLED) {
  // Handle chunk loading errors globally
  window.addEventListener('error', (event) => {
    if (event.error && event.error.name === 'ChunkLoadError') {
      console.error('Global chunk load error detected:', event.error);
      
      // For critical chunks, suggest page reload
      if (event.error.message.includes('Loading chunk')) {
        console.warn('Critical chunk failed to load, attempting recovery...');
        
        // Try to reload the page after a short delay
        setTimeout(() => {
          if (window.confirm('The application needs to reload to recover from a loading error. Continue?')) {
            window.location.reload();
          }
        }, 1000);
      }
    }
  });

  // Handle unhandled promise rejections for chunk errors
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && (
      event.reason.name === 'ChunkLoadError' || 
      event.reason.message.includes('Loading chunk')
    )) {
      console.error('Unhandled chunk loading error:', event.reason);
      event.preventDefault(); // Prevent the default browser error handling
    }
  });
}

// Lazy load all route components for better code splitting
const App = lazyWithRetry(() => import('./App'));
const Download = lazyWithRetry(() => import('./pages/Download'));
const SignIn = lazyWithRetry(() => import('./pages/SignIn'));
const SignUp = lazyWithRetry(() => import('./pages/SignUp'));
const ActivationPending = lazyWithRetry(() => import('./pages/ActivationPending'));
const Activate = lazyWithRetry(() => import('./pages/Activate'));
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'));
const TermsOfService = lazyWithRetry(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const Profile = lazyWithRetry(() => import('./pages/Profile'));
const AIIDE = lazyWithRetry(() => import('./pages/IDE'));
const ProjectPlanning = lazyWithRetry(() => import('./pages/ProjectPlanning'));
const Subscription = lazyWithRetry(() => import('./pages/Subscription'));
const InvitationAccept = lazyWithRetry(() => import('./pages/InvitationAccept'));
const SubscriptionAccessDeniedPage = lazyWithRetry(() => import('./pages/SubscriptionAccessDeniedPage'));

// Detect if we're running in Electron
const isElectron = !!(window.electronAPI || (window.process && window.process.type === 'renderer'));

// Use HashRouter for Electron (needed for file:// protocol) and BrowserRouter for web
const Router = isElectron ? HashRouter : BrowserRouter;

// Define routes with suspense boundaries
const WebRoutes = () => (
  <Routes>
    <Route path="/" element={
      <Suspense fallback={<LazyLoadingFallback component="Landing Page" />}>
        <App />
      </Suspense>
    } />
    <Route path="/download" element={
      <Suspense fallback={<LazyLoadingFallback component="Download" />}>
        <Download />
      </Suspense>
    } />
    <Route path="/signin" element={
      <Suspense fallback={<LazyLoadingFallback component="Sign In" />}>
        <SignIn />
      </Suspense>
    } />
    <Route path="/signup" element={
      <Suspense fallback={<LazyLoadingFallback component="Sign Up" />}>
        <SignUp />
      </Suspense>
    } />
    <Route path="/activation-pending" element={
      <Suspense fallback={<LazyLoadingFallback component="Activation" />}>
        <ActivationPending />
      </Suspense>
    } />
    <Route path="/activate" element={
      <Suspense fallback={<LazyLoadingFallback component="Activation" />}>
        <Activate />
      </Suspense>
    } />
    <Route path="/forgot-password" element={
      <Suspense fallback={<LazyLoadingFallback component="Password Reset" />}>
        <ForgotPassword />
      </Suspense>
    } />
    <Route path="/reset-password" element={
      <Suspense fallback={<LazyLoadingFallback component="Password Reset" />}>
        <ResetPassword />
      </Suspense>
    } />
    <Route path="/terms-of-service" element={
      <Suspense fallback={<LazyLoadingFallback component="Terms" />}>
        <TermsOfService />
      </Suspense>
    } />
    <Route path="/privacy-policy" element={
      <Suspense fallback={<LazyLoadingFallback component="Privacy Policy" />}>
        <PrivacyPolicy />
      </Suspense>
    } />
    <Route path="/subscription" element={
      <Suspense fallback={<LazyLoadingFallback component="Subscription" />}>
        <Subscription />
      </Suspense>
    } />
    <Route path="/invitations/:id" element={
      <Suspense fallback={<LazyLoadingFallback component="Invitation" />}>
        <InvitationAccept />
      </Suspense>
    } />
    <Route path="/dashboard" element={
      <Suspense fallback={<LazyLoadingFallback component="Dashboard" />}>
        <Dashboard />
      </Suspense>
    }>
      <Route path="settings/*" element={
        <Suspense fallback={<LazyLoadingFallback component="Settings" />}>
          <Settings />
        </Suspense>
      } />
      <Route path="profile" element={
        <Suspense fallback={<LazyLoadingFallback component="Profile" />}>
          <Profile />
        </Suspense>
      } />
    </Route>
    <Route path="/projects/:projectId/ide" element={
      <Suspense fallback={<LazyLoadingFallback component="IDE" />}>
        <AIIDE />
      </Suspense>
    } />
    <Route path="/projects/:projectId" element={
      <Suspense fallback={<LazyLoadingFallback component="Project Planning" />}>
        <ProjectPlanning />
      </Suspense>
    } />
    <Route path="/subscription-access-denied" element={
      <Suspense fallback={<LazyLoadingFallback component="Access Denied" />}>
        <SubscriptionAccessDeniedPage />
      </Suspense>
    } />
  </Routes>
);

const ElectronRoutes = () => (
  <Routes>
    {/* Electron app starts with login page as home */}
    <Route path="/" element={
      <Suspense fallback={<LazyLoadingFallback component="Sign In" />}>
        <SignIn />
      </Suspense>
    } />
    <Route path="/signin" element={
      <Suspense fallback={<LazyLoadingFallback component="Sign In" />}>
        <SignIn />
      </Suspense>
    } />
    <Route path="/signup" element={
      <Suspense fallback={<LazyLoadingFallback component="Sign Up" />}>
        <SignUp />
      </Suspense>
    } />
    <Route path="/download" element={
      <Suspense fallback={<LazyLoadingFallback component="Download" />}>
        <Download />
      </Suspense>
    } />
    <Route path="/activation-pending" element={
      <Suspense fallback={<LazyLoadingFallback component="Activation" />}>
        <ActivationPending />
      </Suspense>
    } />
    <Route path="/activate" element={
      <Suspense fallback={<LazyLoadingFallback component="Activation" />}>
        <Activate />
      </Suspense>
    } />
    <Route path="/forgot-password" element={
      <Suspense fallback={<LazyLoadingFallback component="Password Reset" />}>
        <ForgotPassword />
      </Suspense>
    } />
    <Route path="/reset-password" element={
      <Suspense fallback={<LazyLoadingFallback component="Password Reset" />}>
        <ResetPassword />
      </Suspense>
    } />
    <Route path="/terms-of-service" element={
      <Suspense fallback={<LazyLoadingFallback component="Terms" />}>
        <TermsOfService />
      </Suspense>
    } />
    <Route path="/privacy-policy" element={
      <Suspense fallback={<LazyLoadingFallback component="Privacy Policy" />}>
        <PrivacyPolicy />
      </Suspense>
    } />
    <Route path="/subscription" element={
      <Suspense fallback={<LazyLoadingFallback component="Subscription" />}>
        <Subscription />
      </Suspense>
    } />
    <Route path="/invitations/:id" element={
      <Suspense fallback={<LazyLoadingFallback component="Invitation" />}>
        <InvitationAccept />
      </Suspense>
    } />
    <Route path="/dashboard" element={
      <Suspense fallback={<LazyLoadingFallback component="Dashboard" />}>
        <Dashboard />
      </Suspense>
    }>
      <Route path="settings/*" element={
        <Suspense fallback={<LazyLoadingFallback component="Settings" />}>
          <Settings />
        </Suspense>
      } />
      <Route path="profile" element={
        <Suspense fallback={<LazyLoadingFallback component="Profile" />}>
          <Profile />
        </Suspense>
      } />
    </Route>
    <Route path="/projects/:projectId/ide" element={
      <Suspense fallback={<LazyLoadingFallback component="IDE" />}>
        <AIIDE />
      </Suspense>
    } />
    <Route path="/projects/:projectId" element={
      <Suspense fallback={<LazyLoadingFallback component="Project Planning" />}>
        <ProjectPlanning />
      </Suspense>
    } />
    <Route path="/subscription-access-denied" element={
      <Suspense fallback={<LazyLoadingFallback component="Access Denied" />}>
        <SubscriptionAccessDeniedPage />
      </Suspense>
    } />
  </Routes>
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ChunkErrorBoundary>
      <Router>
        {/* TitleBar appears on every page in Electron */}
        <TitleBar />
        <div 
          className="app-content" 
          style={{ 
            height: isElectron ? 'calc(100vh - 36px)' : '100vh',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {isElectron ? <ElectronRoutes /> : <WebRoutes />}
        </div>
      </Router>
    </ChunkErrorBoundary>
  </React.StrictMode>
);
